import { getInvoices } from './SalesService';
import { getReceivedInvoices, getSummaryInvoices } from './ElectronicBillingService';

const PAGE_SIZE = 500;
const MAX_PAGES = 200;

const mapCfeTypeToLabel = (type) => {
  switch (String(type || '')) {
    case '101':
      return 'Ticket';
    case '111':
      return 'Factura';
    case '112':
      return 'NC-Factura';
    case '102':
      return 'NC-Ticket';
    default:
      return type ? `Tipo ${type}` : '';
  }
};

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('es-UY');
};

const numberOrBlank = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : '';
};

const textOrBlank = (value) => (value == null ? '' : String(value));

const xmlEscape = (value) => textOrBlank(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const sanitizeSheetName = (name) => textOrBlank(name)
  .replace(/[\[\]:*?/\\]/g, ' ')
  .slice(0, 31)
  .trim() || 'Hoja';

const sanitizeFileName = (name) => textOrBlank(name)
  .replace(/[^a-zA-Z0-9._-]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

const makeCell = (value) => {
  const isNumber = typeof value === 'number' && Number.isFinite(value);
  return `<Cell><Data ss:Type="${isNumber ? 'Number' : 'String'}">${xmlEscape(value)}</Data></Cell>`;
};

const makeWorksheet = (name, headers, rows) => {
  const headerRow = `<Row>${headers.map((header) => makeCell(header)).join('')}</Row>`;
  const dataRows = rows.map((row) => (
    `<Row>${headers.map((header) => makeCell(row[header])).join('')}</Row>`
  )).join('');

  return `
    <Worksheet ss:Name="${xmlEscape(sanitizeSheetName(name))}">
      <Table>${headerRow}${dataRows}</Table>
    </Worksheet>
  `;
};

const buildWorkbookXml = (worksheets) => `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook
  xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:x="urn:schemas-microsoft-com:office:excel"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:html="http://www.w3.org/TR/REC-html40">
  ${worksheets.join('')}
</Workbook>`;

const downloadWorkbook = (xml, filename) => {
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const fetchAllPages = async (fetcher, params) => {
  const allRows = [];
  const seenIds = new Set();

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = await fetcher({ ...params, page, pageSize: PAGE_SIZE, search: '' });
    const rows = data?.invoices || [];

    rows.forEach((row, index) => {
      const id = row?.id ?? `${row?.serie ?? ''}-${row?.nro ?? ''}-${row?.rucEmisor ?? ''}-${page}-${index}`;
      if (!seenIds.has(id)) {
        seenIds.add(id);
        allRows.push(row);
      }
    });

    if (rows.length < PAGE_SIZE) break;
  }

  return allRows;
};

const mapIssuedInvoice = (invoice) => ({
  ID: invoice.id,
  Fecha: formatDate(invoice.date ?? invoice.fecha ?? invoice.createdAt),
  Serie: invoice.serie,
  Numero: invoice.nro,
  'Tipo CFE': mapCfeTypeToLabel(invoice.type ?? invoice.tipoCfe),
  Cliente: invoice.client || invoice.clientName || '',
  Estado: invoice.status,
  Moneda: invoice.currency || invoice.moneda || 'UYU',
  Total: numberOrBlank(invoice.total ?? invoice.montoTotal),
});

const mapReceivedInvoice = (invoice) => ({
  Serie: invoice.serie,
  Numero: invoice.nro,
  'Tipo CFE': mapCfeTypeToLabel(invoice.tipoCfe),
  Fecha: formatDate(invoice.fecha),
  Proveedor: invoice.emisorFantasyName || invoice.emisorName || '',
  'Razon social proveedor': invoice.emisorName || '',
  'RUC proveedor': invoice.rucEmisor || '',
  Moneda: invoice.moneda || '',
  IVA: numberOrBlank(invoice.iva),
  Total: numberOrBlank(invoice.montoTotal),
});

const mapSummary = (summary, { rut, month, year }) => ([
  { Campo: 'RUT', Valor: rut },
  { Campo: 'Mes', Valor: month },
  { Campo: 'Anio', Valor: year },
  { Campo: 'Estado', Valor: summary?.status || '' },
  { Campo: 'IVA venta', Valor: numberOrBlank(summary?.ivaVenta) },
  { Campo: 'IVA compra', Valor: numberOrBlank(summary?.ivaCompra) },
  { Campo: 'Resguardo', Valor: numberOrBlank(summary?.resguardo) },
  { Campo: 'Saldo mes anterior', Valor: numberOrBlank(summary?.saldoMesAnterior) },
  { Campo: 'Saldo', Valor: numberOrBlank(summary?.saldo) },
  { Campo: 'IVA a pagar', Valor: numberOrBlank(summary?.ivaAPagar) },
  { Campo: 'Cantidad emitidas', Valor: numberOrBlank(summary?.cantidadEmitidas) },
  { Campo: 'Cantidad recibidas', Valor: numberOrBlank(summary?.cantidadRecibidas) },
]);

export const exportAccountingExcel = async ({ rut, month, year }) => {
  const params = { rut, month, year };
  const [summary, issuedInvoices, receivedInvoices] = await Promise.all([
    getSummaryInvoices(params).catch(() => null),
    fetchAllPages(getInvoices, params),
    fetchAllPages(getReceivedInvoices, params),
  ]);

  const worksheets = [
    makeWorksheet('Resumen', ['Campo', 'Valor'], mapSummary(summary, params)),
    makeWorksheet('Facturas emitidas', ['ID', 'Fecha', 'Serie', 'Numero', 'Tipo CFE', 'Cliente', 'Estado', 'Moneda', 'Total'], issuedInvoices.map(mapIssuedInvoice)),
    makeWorksheet('Facturas recibidas', ['Serie', 'Numero', 'Tipo CFE', 'Fecha', 'Proveedor', 'Razon social proveedor', 'RUC proveedor', 'Moneda', 'IVA', 'Total'], receivedInvoices.map(mapReceivedInvoice)),
  ];

  const filename = `${sanitizeFileName(`contabilidad-${rut}-${year}-${String(month).padStart(2, '0')}`)}.xls`;
  downloadWorkbook(buildWorkbookXml(worksheets), filename);

  return {
    issuedCount: issuedInvoices.length,
    receivedCount: receivedInvoices.length,
  };
};
